import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const regatesRoyalesCannesEn: ChauffeurPage = {
  slug: "regates-royales-cannes",
  title: "Private chauffeur Régates Royales de Cannes – VTC",
  subtitle:
    "Private chauffeur during the Régates Royales de Cannes: access to the old port and the Yacht Club, transfers from Nice airport and evenings.",
  keywords: [
    "régates royales cannes chauffeur",
    "cannes classic yacht regatta chauffeur",
    "cannes private driver regatta",
  ],
  hero: {
    title: "Private chauffeur during the Régates Royales de Cannes",
    subtitle:
      "Classic yachts in the bay: a dedicated chauffeur when the old port comes alive and parking runs short in Cannes.",
    keywords: [
      "Régates Royales de Cannes chauffeur",
      "Cannes old port VTC",
      "Nice airport Cannes transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Régates Royales de Cannes",
    badges: ["Airport welcome", "Old-port access", "Evenings & returns"],
  },
  benefits: {
    heading: "Why a chauffeur during the Régates Royales",
    items: [
      {
        title: "Old port and Croisette handled",
        description:
          "The regatta village takes over the old port. The chauffeur knows the authorised drop-offs nearest the Yacht Club and the Croisette.",
      },
      {
        title: "Nice airport meet-and-greet",
        description:
          "Nice Côte d'Azur is under 30 km away. Name-board welcome, flight tracking and a direct transfer to your Cannes hotel.",
      },
      {
        title: "Scarce parking solved",
        description:
          "In late September, parking near the port of Cannes is difficult: the chauffeur drops you as close as possible and leaves.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Régates Royales de Cannes",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Cannes transfer with flight tracking during the regatta week.",
      },
      {
        title: "Old-port & Yacht Club access",
        text: "Drop-off as close as possible to the quays, the regatta village and the Yacht Club de Cannes.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to chain the port, lunches on the Croisette and prize-givings.",
      },
      {
        title: "Evenings & late-night returns",
        text: "Dinners and parties in Cannes or the hinterland, with a guaranteed late return.",
      },
      {
        title: "Guest & crew shuttles",
        text: "Multi-vehicle coordination for guests, owners and crews.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Régates Royales",
    items: [
      {
        name: "Old port of Cannes",
        description:
          "Quai Saint-Pierre, the regatta village and the starts in the bay.",
      },
      {
        name: "Yacht Club de Cannes",
        description: "Host of the Régates Royales and a meeting point.",
      },
      {
        name: "La Croisette",
        description: "Palaces, private beaches and seafront hotels.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Under 30 km away, Terminals 1 and 2.",
      },
      {
        name: "Le Suquet & hinterland",
        description: "Old town and dinners outside the centre.",
      },
    ],
  },
  generic: {
    heading: "The Régates Royales de Cannes from a chauffeur's seat",
    subheading:
      "Every year in late September, classic yachts gather in the bay of Cannes around the old port.",
    paragraphs: [
      "Every autumn, in late September, the Régates Royales bring magnificent classic yachts together in the bay of Cannes. The regatta village takes over the old port, near the Yacht Club, and the centre fills up: parking becomes scarce and the quaysides are congested.",
      "Nice Côte d'Azur airport is under thirty kilometres away. With flight tracking and a name-board welcome, the transfer to your Croisette hotel runs without waiting, even at peak times.",
      "Between regattas, lunches on the Croisette and evenings, the pace is intense. Hourly hire keeps the same chauffeur all day and secures the late-night returns after the prize-givings.",
    ],
    bullets: ["Old-port access", "Airport under 30 km", "Late-night returns"],
  },
  process: {
    heading: "Booking a chauffeur for the Régates Royales de Cannes",
    steps: [
      "Tell us your regatta dates, your hotel and your timings.",
      "We plan the drop-offs around access to the old port and the Croisette crowds.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover the port, lunches and evenings.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during the Régates Royales de Cannes?",
      a: "Book early: in late September, Cannes is in high demand and the regatta week draws many guests. Share your dates, your hotel and your timings so we can secure a dedicated chauffeur.",
    },
    {
      q: "Can the chauffeur get me close to the old port and the Yacht Club?",
      a: "Yes. The regatta village occupies the old port and the surroundings are congested. The chauffeur drops you at the nearest authorised point to the Yacht Club de Cannes, then collects you at the agreed spot.",
    },
    {
      q: "Do you handle transfers from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is under 30 km from Cannes: the transfer to your Croisette hotel is quick, even in high season.",
    },
    {
      q: "Is parking difficult in Cannes during the regattas?",
      a: "Yes. In late September, the central car parks fill up and the port surroundings are congested. The chauffeur saves you the search for a space: he drops off, waits or returns at the agreed time.",
    },
    {
      q: "Do you cover late returns after the evenings?",
      a: "Yes. Dinners and prize-givings finish late: the chauffeur waits and guarantees your direct return to the hotel, in Cannes or elsewhere on the Riviera.",
    },
    {
      q: "What vehicles do you offer?",
      a: "Saloons, SUVs and vans with a chauffeur depending on your group size, for a comfortable ride between the hotel, the port and the evenings.",
    },
    {
      q: "Can you coordinate several vehicles for a crew or a group?",
      a: "Yes. We arrange multi-vehicle shuttles for guests, owners and crews, with the same level of punctuality for everyone.",
    },
  ],
  relatedLinks: [
    {
      label: "Airport transfers in Cannes",
      href: "/en/chauffeur/cannes",
    },
    {
      label: "Book an airport transfer",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car Cannes",
      href: "/en/location/car-rental-cannes",
    },
  ],
  cta: {
    title: "Your chauffeur for the Régates Royales de Cannes",
    text: "Airport welcome, old-port access and late-night returns: book a dedicated chauffeur for the regatta week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Régates Royales Cannes Chauffeur | KS Rent",
    description:
      "Private chauffeur during the Régates Royales de Cannes: Nice airport transfer, access to the old port and the Yacht Club, daily hire and late-night returns.",
    canonical: "/en/chauffeur/regates-royales-cannes",
  },
};

export default regatesRoyalesCannesEn;
